import React, { useState, useEffect } from "react";
import Button from "@amzn/meridian/esm/components/button";
import Box from "@amzn/meridian/esm/components/box";
import Column from "@amzn/meridian/esm/components/column";
import Loader from "@amzn/meridian/esm/components/loader";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme/theme";
import InformSummarizeModal from "./InformSummarizeModal";
import SummarizingIndicator from "./SummarizingIndicator";
import SummarizeStopped from "./SummarizeStopped";
import aimsTheme from "../../meridian-theme-aims.json";
import star from "../../images/star.svg";
import zoomSdk from "@zoom/appssdk";
import ServerNotRespondingModal from "../common/ServerNotRespondingModal";
import LogManager from "../utils/LogManager";
import { AIMS_STATUS, TRANSCRIPT_START_CHAT_MESSAGE } from "../../constants";

/**
 * This component is shown to the meeting host when AiMS is available for the meeting.
 *
 * Key Features:
 * - Renders a "Summarize" button to start RTMS.
 * - On first click, it shows an informational modal (`InformSummarizeModal`) to confirm the user understands the consent.
 * - On subsequent clicks, it triggers `startRTMS` via Zoom SDK.(TODO: will update to call backend api)
 * - Once summarization is active, the UI switches to an "End" button which stops RTMS.
 * - If RTMS start/stop fails, a `ServerNotRespondingModal` is shown for error feedback.
 * - When summarization ends, a temporary confirmation banner (`SummarizeStopped`) appears for 2 minutes.
 * - A `SummarizingIndicator` is shown during active summarization.
 */

const SummarizeBox = ({ isSummarizing, setIsSummarizing, meetingId, meetingUuid, aimsStatus }) => {
  const [isInformSummarizeModalOpen, setIsInformSummarizeModalOpen] = useState(false);
  const [summaryStopped, setSummaryStopped] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [summarizeClickCount, setSummarizeClickCount] = useState(0);
  const [isServerErrorModalOpen, setIsServerErrorModalOpen] = useState(false);

  useEffect(() => {
    // Localhost testing override
    if (window.location.href === "http://localhost:3000/") {
      setIsInformSummarizeModalOpen(false);
      setSummaryStopped(false);
      setIsServerErrorModalOpen(false);
    }
  }, []);

  //TODO: add more checks
  const onEndSummarize = async () => {
    if (isLoading) return;
    setIsLoading(true);
    LogManager.info("End button clicked, call stopping RTMS API...");

    let rtmsResponse = {};
    if (window.location.href === "http://localhost:3000/") {
      console.log("[Localhost] Simulating stop RTMS call.");
      await new Promise(resolve => setTimeout(resolve, 2000)); // 2 seconds delay
      rtmsResponse.result = "OK"; // or "Fail" to simulate error
      setSummaryStopped(true);
    } else {
      try {
        const data = {
          action: "stop",
          role: "host",
          meetingUuid: meetingUuid,
          meetingId: meetingId,
        };
        LogManager.info("Calling /rtms-status-update to stop RTMS. Request body is: " + JSON.stringify(data));

        const response = await fetch("/rtms-status-update", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        });

        LogManager.info("Full /rtms-status-update api response is ", response);
        const responseJson = await response.json();
        LogManager.info("The responseJson of /rtms-status-update is: ", responseJson);

        let parsedPayload = JSON.parse(responseJson.Payload);
        //sample: "{\"action\":\"stop\",\"alias\":\"mznwng\",\"meetingId\":\"1234567890\",\"result\":\"OK\"}"
        LogManager.info("Parsed RtmsResponse payload from result is:", parsedPayload);

        rtmsResponse = JSON.parse(parsedPayload);
      } catch (e) {
        console.error(e);
        LogManager.error("Error calling /rtms-status-update:", e);
        rtmsResponse = "Error";
      }
    }

    if (rtmsResponse.result !== "OK") {
      setIsServerErrorModalOpen(false);
      // Force re-render by toggling the modal state
      setTimeout(() => setIsServerErrorModalOpen(true), 0);
      return;
    }

    setIsSummarizing(false);
    setSummaryStopped(true);

    // Show SummaryStopped component for 2 minutes
    setTimeout(() => {
      setSummaryStopped(false);
    }, 120000);

    setIsLoading(false);
  };

  const onModalConfirmed = () => {
    setIsInformSummarizeModalOpen(false);
    sendTranscriptStartToChat();
    onSummarize();
  };

  const sendTranscriptStartToChat = async () => {
    try {
      await zoomSdk.sendMessageToChat({
        message: TRANSCRIPT_START_CHAT_MESSAGE
      });
      LogManager.info("Sent transcription disclaimer message");
    } catch (error) {
      LogManager.error("Error sending disclaimer message:", error);
    }
  };

  //TODO: Add more checks
  const onSummarize = async () => {
    if (isLoading) return;
    setIsLoading(true);
    setSummaryStopped(false);
    LogManager.info("Attempting to start RTMS...");

    let rtmsResponse = {};
    if (window.location.href === "http://localhost:3000/") {
      console.log("[Localhost] Simulating startRTMS call: Success");
      await new Promise(resolve => setTimeout(resolve, 2000)); // 2 seconds delay
      rtmsResponse.result = "OK";
    } else {
      try {
        //TODO: this is for testing
        // zoomSdk.sendMessageToChat("[Develonging] this is for testing..");

        const data = {
          action: "start",
          role: "host",
          meetingUuid: meetingUuid,
          meetingId: meetingId,
        };
        LogManager.info("Calling /rtms-status-update. Request body is: " + JSON.stringify(data));

        const response = await fetch("/rtms-status-update", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        });
        LogManager.info("Full /rtms-status-update api response is ", response);
        const responseJson = await response.json();
        LogManager.info("The responseJson of /rtms-status-update is: ", responseJson);

        let parsedPayload = JSON.parse(responseJson.Payload);
        //sample: "{\"action\":\"start\",\"alias\":\"mznwng\",\"meetingId\":\"1234567890\",\"result\":\"OK\"}"
        LogManager.info("Parsed RtmsResponse payload from result is:", parsedPayload);

        rtmsResponse = JSON.parse(parsedPayload);
      } catch (e) {
        LogManager.error("Error calling /rtms-status-update:", e);
        rtmsResponse = "Error";
      }
    }

    LogManager.info("The /rtms-status-update response:", rtmsResponse);

    if (rtmsResponse.result !== "OK") {
      setIsServerErrorModalOpen(false);
      setTimeout(() => setIsServerErrorModalOpen(true), 0);
    } else {
      setIsSummarizing(true);
      setSummarizeClickCount((prev) => prev + 1);
    }
    setIsLoading(false);
  };

  return (
    <Theme palette={aimsTheme}>
      <Column alignmentHorizontal="start" spacing="none">
        <Column height="40px"></Column>
        <Column height="100px" spacing="none">
          <Row width="100%" spacing="none" spacingInset="none">
            <Theme tokens={{ boxBorderColorOutline: "#FFFFFF" }}>
              <Box type="outline" spacingInset="none" minWidth="72px"></Box>
            </Theme>
            <Theme tokens={{ boxBorderColorOutline: "#E7E9E9" }}>
              <Box type="outline" spacingInset="400" width="226px" height="100px">
                <Row spacing="none">
                  <img src={star} alt="star" />
                  <Theme tokens={{ boxBorderColorOutline: "#FFFFFF" }}>
                    <Box type="outline" spacingInset="none" minWidth="20px"></Box>
                  </Theme>
                  <Text fontFamily="amazonEmber" type="h50" alignment="start">
                    Summarize meeting
                  </Text>
                </Row>
                <Column height="12px"></Column>
                <Theme
                  tokens={{
                    buttonBackgroundColorPrimaryDefault: isSummarizing ? "#AF1F18" : "#006D5B",
                    buttonBackgroundColorPrimaryHover: isSummarizing ? "#8C1913" : "#005345",
                    buttonBackgroundColorPrimaryPressed: isSummarizing ? "#6E140F" : "#003F33",
                  }}
                >
                  {isLoading ? (
                    <Row alignmentHorizontal="center">
                      <Loader size="medium"/>
                    </Row>
                  ) : isSummarizing ? (
                    <Button size="small" minWidth="194px" onClick={onEndSummarize}>
                      End
                    </Button>
                  ) : (
                    <>
                      <Button
                        size="small"
                        minWidth="194px"
                        onClick={() => {
                          if (summarizeClickCount === 0) {
                            setIsInformSummarizeModalOpen(true);
                          } else {
                            onSummarize(); // second+ click: directly start summarize
                          }
                        }}
                      >
                        Summarize
                      </Button>
                    </>
                  )}
                </Theme>
                {isServerErrorModalOpen ? (
                  <ServerNotRespondingModal isOpen={true} onClose={() => setIsServerErrorModalOpen(false)} />
                ) : (
                  <InformSummarizeModal
                    isOpen={isInformSummarizeModalOpen}
                    onClose={() => setIsInformSummarizeModalOpen(false)}
                    onConfirm={onModalConfirmed}
                  />
                )}
              </Box>
            </Theme>
          </Row>
        </Column>
        <Column height="32px"></Column>
        {summaryStopped && <SummarizeStopped aimsStatus={aimsStatus} />}
        {isSummarizing && <SummarizingIndicator />}
      </Column>
    </Theme>
  );
};

export default SummarizeBox;
